/**
 * ProductionIzing ProjectFlow Project Data
 * Taking ProjectFlow from prototype to production-ready SaaS platform
 */

import type { Project, Task, Epic, Sprint, Priority, TaskStatus } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-productionize-projectflow';

export const productionizeProject: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'ProductionIzing ProjectFlow',
  description: 'Transform ProjectFlow from a functional prototype into a production-ready, scalable SaaS platform with enterprise features, security hardening, performance optimization, and comprehensive monitoring',
  status: 'Approved',
  proposal: `# ProductionIzing ProjectFlow - SaaS Platform Development

## Executive Summary
Transform the current ProjectFlow prototype into a production-ready SaaS platform capable of serving thousands of enterprise customers with mission-critical project management needs.

## Vision
Build a world-class project management platform that combines the simplicity of modern tools with the power and flexibility required by enterprise teams.

## Key Objectives
1. **Scalability**: Handle 10,000+ concurrent users and 1M+ projects
2. **Security**: Enterprise-grade security with SOC 2 compliance
3. **Performance**: Sub-second page loads and real-time updates
4. **Reliability**: 99.9% uptime SLA with zero data loss
5. **Multi-Tenancy**: Complete data isolation and white-label support
6. **Mobile First**: Native mobile apps for iOS and Android
7. **Integrations**: Connect with 50+ popular tools (Jira, Slack, GitHub, etc.)
8. **Enterprise Features**: SSO, SAML, advanced RBAC, audit logs
9. **AI/ML**: Intelligent task recommendations and predictive analytics
10. **Global Scale**: Multi-region deployment with CDN

## Technology Stack

### Backend
- **Language**: Go 1.21+ for API services
- **Framework**: Gin/Fiber for high-performance REST APIs
- **Database**: PostgreSQL 15 (primary), TimescaleDB for analytics
- **Cache**: Redis Cluster for session management and caching
- **Message Queue**: Apache Kafka for event streaming
- **Search**: Elasticsearch for full-text search
- **Storage**: AWS S3/CloudFlare R2 for file storage

### Frontend
- **Web**: React 18 + Next.js 14 (App Router)
- **Mobile**: React Native 0.73+
- **State Management**: Zustand + React Query
- **UI Framework**: Tailwind CSS + shadcn/ui
- **Real-time**: WebSocket (Socket.io/Pusher)

### Infrastructure
- **Cloud**: AWS (primary), GCP (backup)
- **Orchestration**: Kubernetes (EKS)
- **IaC**: Terraform + ArgoCD
- **CI/CD**: GitHub Actions
- **Monitoring**: Datadog, Sentry, Prometheus/Grafana
- **CDN**: CloudFlare
- **Email**: SendGrid/AWS SES

## Success Metrics
- Load time < 1 second (p95)
- API response time < 100ms (p95)
- 99.9% uptime
- Zero security incidents
- 100% data durability
- Customer satisfaction > 4.5/5
- Net Promoter Score (NPS) > 50

## Timeline
- Phase 1 (Months 1-3): Infrastructure & Security
- Phase 2 (Months 4-6): Performance & Scalability
- Phase 3 (Months 7-9): Enterprise Features
- Phase 4 (Months 10-12): Mobile Apps & Integrations
- Total Duration: 12 months`,
  costs: [
    { id: 'cost-pf-dev', description: 'Development Team (8 engineers × 12 months)', amount: 960000, category: 'Personnel' },
    { id: 'cost-pf-devops', description: 'DevOps Engineers (2 FTE × 12 months)', amount: 240000, category: 'Personnel' },
    { id: 'cost-pf-qa', description: 'QA Engineers (3 FTE × 12 months)', amount: 270000, category: 'Personnel' },
    { id: 'cost-pf-pm', description: 'Product Manager + Designers (3 FTE × 12 months)', amount: 360000, category: 'Personnel' },
    { id: 'cost-pf-infra', description: 'AWS Infrastructure (12 months)', amount: 120000, category: 'Infrastructure' },
    { id: 'cost-pf-services', description: 'SaaS Services (Datadog, Sentry, etc.)', amount: 60000, category: 'Services' },
    { id: 'cost-pf-licenses', description: 'Software Licenses & Tools', amount: 30000, category: 'Software' },
  ],
  teamMembers: [
    { userId: 'hossainzarif2002@gmail.com', role: 'developer' },
    { userId: 'user-rafi', role: 'developer' },
    { userId: 'user-sakib', role: 'developer' },
    { userId: 'pemsapi', role: 'developer' },
    { userId: 'user-saif', role: 'developer' },
    { userId: 'asif.redmond@gmail.com', role: 'developer' },
    { userId: 'aidimentry', role: 'stakeholder' },
    { userId: 'user-khaled', role: 'stakeholder' },
  ],
  createdAt: '2026-02-23T00:00:00Z',
  updatedAt: '2026-02-23T00:00:00Z',
  externalLinks: [
    { type: 'GitHub Repo', url: 'https://github.com/intellesphere/projectflow' },
    { type: 'Figma Designs', url: 'https://figma.com/projectflow-design' },
    { type: 'Product Roadmap', url: 'https://productboard.com/projectflow' },
  ],
  startDate: '2026-03-01',
  targetEndDate: '2027-02-28',
  costPerHour: 120,
};

export const productionizeEpics: Epic[] = [
  {
    id: 'epic-pf-infrastructure',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Infrastructure & DevOps',
    description: 'Kubernetes cluster setup, CI/CD pipelines, IaC with Terraform, multi-region deployment',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-auth',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Authentication & Multi-Tenancy',
    description: 'SSO, SAML 2.0, OAuth 2.0, JWT, complete tenant isolation, white-label support',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-performance',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Performance & Scalability',
    description: 'Database optimization, caching strategy, horizontal scaling, load testing, CDN integration',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-security',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Security & Compliance',
    description: 'SOC 2 Type II compliance, encryption at rest/transit, security audit, penetration testing, GDPR compliance',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-enterprise',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Enterprise Features',
    description: 'Advanced RBAC, audit logs, custom fields, workflow automation, SLA management, dedicated support portal',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-mobile',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Mobile App Development',
    description: 'Native iOS and Android apps with offline support, push notifications, biometric authentication',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-api',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'API & Integrations',
    description: 'Public REST API v2, GraphQL API, webhooks, Zapier/Make integration, OAuth apps',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-pf-monitoring',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Monitoring & Analytics',
    description: 'Real-time dashboards, error tracking, performance monitoring, business analytics, usage metrics',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 120,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
];

export const productionizeTasks: Task[] = [
  // Infrastructure & DevOps Epic
  {
    id: 'PF-INF-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-infrastructure',
    title: 'Setup EKS Kubernetes Cluster',
    description: 'Provision production-ready EKS cluster with auto-scaling, multi-AZ deployment, and security groups',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-INF-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-infrastructure',
    title: 'Terraform Infrastructure as Code',
    description: 'Write Terraform modules for VPC, EKS, RDS, ElastiCache, S3, and all AWS resources',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: ['PF-INF-001'],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-INF-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-infrastructure',
    title: 'ArgoCD GitOps Setup',
    description: 'Configure ArgoCD for continuous deployment, sync policies, and automated rollbacks',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: ['PF-INF-001'],
    subtaskIds: [],
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-INF-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-infrastructure',
    title: 'CI/CD Pipeline with GitHub Actions',
    description: 'Build comprehensive CI/CD pipeline with testing, security scanning, and deployment stages',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'DevOps',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-INF-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-infrastructure',
    title: 'Multi-Region Setup (US-East + EU-West)',
    description: 'Deploy infrastructure in two regions with data replication and failover capabilities',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: ['PF-INF-002'],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Authentication & Multi-Tenancy Epic
  {
    id: 'PF-AUTH-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-auth',
    title: 'Implement SSO with SAML 2.0',
    description: 'Add single sign-on support for enterprise customers using SAML 2.0 protocol (Okta, Azure AD, Google Workspace)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Authentication',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-AUTH-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-auth',
    title: 'OAuth 2.0 Provider for Third-Party Apps',
    description: 'Build OAuth 2.0 authorization server for third-party integrations',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'Authentication',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-AUTH-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-auth',
    title: 'Multi-Tenant Database Isolation',
    description: 'Implement complete database-level tenant isolation with row-level security policies',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-AUTH-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-auth',
    title: 'White-Label Support (Custom Domains)',
    description: 'Allow customers to use custom domains (e.g., pm.company.com) with SSL certificates',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Performance & Scalability Epic
  {
    id: 'PF-PERF-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-performance',
    title: 'Database Query Optimization',
    description: 'Optimize all slow queries, add proper indexes, implement query result caching',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Performance',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-PERF-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-performance',
    title: 'Redis Caching Layer',
    description: 'Implement comprehensive Redis caching for frequently accessed data (projects, tasks, users)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Performance',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-PERF-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-performance',
    title: 'CloudFlare CDN Integration',
    description: 'Configure CloudFlare CDN for static assets, images, and edge caching',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-PERF-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-performance',
    title: 'Horizontal Pod Autoscaling',
    description: 'Configure HPA for all services to automatically scale based on CPU/memory/custom metrics',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-PERF-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-performance',
    title: 'Load Testing & Benchmarking',
    description: 'Comprehensive load testing with k6/Locust, identify bottlenecks, establish performance baselines',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Testing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Security & Compliance Epic
  {
    id: 'PF-SEC-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-security',
    title: 'SOC 2 Type II Compliance Preparation',
    description: 'Document security controls, implement compliance requirements, prepare for audit',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Compliance',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 15,
    estimatedHours: 120,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-SEC-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-security',
    title: 'Encryption at Rest and in Transit',
    description: 'Implement AES-256 encryption for database, TLS 1.3 for all communications, KMS integration',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Security',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-SEC-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-security',
    title: 'Penetration Testing',
    description: 'Engage external security firm for comprehensive penetration testing and remediation',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Security',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-SEC-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-security',
    title: 'GDPR Compliance Implementation',
    description: 'Data subject rights (access, deletion, portability), consent management, DPO designation',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Compliance',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Enterprise Features Epic
  {
    id: 'PF-ENT-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-enterprise',
    title: 'Advanced RBAC System',
    description: 'Implement granular role-based access control with custom roles, permissions, and resource-level access',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-ENT-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-enterprise',
    title: 'Comprehensive Audit Logging',
    description: 'Log all user actions, API calls, and system events with searchable audit trail',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-ENT-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-enterprise',
    title: 'Custom Fields & Workflows',
    description: 'Allow customers to define custom fields and automate workflows with visual workflow builder',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 12,
    estimatedHours: 96,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-ENT-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-enterprise',
    title: 'SLA Management & Tracking',
    description: 'Define and track SLAs for projects and tasks with automated escalations',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Mobile App Development Epic
  {
    id: 'PF-MOB-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-mobile',
    title: 'React Native App Setup',
    description: 'Initialize React Native project with proper architecture, navigation, and state management',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Mobile',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MOB-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-mobile',
    title: 'Core Features Implementation (iOS & Android)',
    description: 'Implement task management, project views, notifications, and real-time updates',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Mobile',
    assigneeIds: [],
    parentTaskIds: ['PF-MOB-001'],
    subtaskIds: [],
    estimatedDays: 20,
    estimatedHours: 160,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MOB-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-mobile',
    title: 'Offline Support with Sync',
    description: 'Implement offline mode with local storage and background synchronization',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Mobile',
    assigneeIds: [],
    parentTaskIds: ['PF-MOB-002'],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MOB-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-mobile',
    title: 'Push Notifications & Deep Linking',
    description: 'Implement FCM/APNS push notifications with deep linking to specific tasks/projects',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Mobile',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MOB-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-mobile',
    title: 'App Store Submission (iOS & Android)',
    description: 'Prepare app metadata, screenshots, submit to App Store and Play Store',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Mobile',
    assigneeIds: [],
    parentTaskIds: ['PF-MOB-002', 'PF-MOB-003'],
    subtaskIds: [],
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // API & Integrations Epic
  {
    id: 'PF-API-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-api',
    title: 'Public REST API v2',
    description: 'Design and implement comprehensive public REST API with versioning and rate limiting',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'API',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 12,
    estimatedHours: 96,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-API-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-api',
    title: 'GraphQL API',
    description: 'Implement GraphQL API for flexible data fetching with subscriptions for real-time updates',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'API',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-API-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-api',
    title: 'Webhook System',
    description: 'Build robust webhook delivery system with retry logic, signature verification, and event filtering',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'API',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-API-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-api',
    title: 'Integration Marketplace (Jira, Slack, GitHub)',
    description: 'Build integrations with popular tools: Jira, Slack, GitHub, GitLab, Trello, Asana',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 15,
    estimatedHours: 120,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-API-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-api',
    title: 'Zapier/Make Integration',
    description: 'Build Zapier app with triggers and actions for workflow automation',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Monitoring & Analytics Epic
  {
    id: 'PF-MON-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-monitoring',
    title: 'Datadog APM Integration',
    description: 'Integrate Datadog for application performance monitoring, distributed tracing, and metrics',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Monitoring',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MON-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-monitoring',
    title: 'Sentry Error Tracking',
    description: 'Configure Sentry for error tracking, performance monitoring, and release tracking',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Monitoring',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MON-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-monitoring',
    title: 'Business Analytics Dashboard',
    description: 'Build internal dashboard for customer usage metrics, growth analytics, and KPIs',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Analytics',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PF-MON-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-pf-monitoring',
    title: 'SLA Monitoring & Alerts',
    description: 'Set up uptime monitoring, SLA tracking, and automated alerts via PagerDuty',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Monitoring',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];

export const productionizeSprint1: Sprint = {
  id: 'sprint-pf-infra-1',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'Sprint 1: Infrastructure Foundation',
  startDate: '2026-03-01',
  endDate: '2026-03-15',
  status: 'Active',
  taskIds: ['PF-INF-001', 'PF-INF-002', 'PF-INF-003', 'PF-INF-004'],
  goals: 'Set up production-ready Kubernetes cluster, Terraform IaC, and CI/CD pipeline. By the end of this sprint, we should have a fully automated deployment system.',
  plannedVelocity: 4,
  actualVelocity: 0,
  createdAt: '2026-02-23T00:00:00Z',
  updatedAt: '2026-02-23T00:00:00Z',
};
